import { Delete as DeleteIcon, MoreVert } from "@mui/icons-material";
import { Avatar, Box, Chip, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { pageDialogOpen } from "../../../state/app";
import { type Organization } from "./types";

const tDeleteOrganizationTooltip = "Opens the delete organization confirmation modal.";
const tDelete = "Delete";

interface OrganizationItemProps {
  organization: Organization;
  isSiteAdmin: boolean;
}

const OrganizationItem = ({ organization, isSiteAdmin }: OrganizationItemProps) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    handleMenuClose();
    dispatch(
      pageDialogOpen({
        contentProps: { organizationId: organization.id, organizationName: organization.name },
        contentType: "delete-organization",
      }),
    );
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        position: "relative",
        width: "100%",
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", flex: "0 1 auto", minWidth: 0 }}>
        <Avatar
          alt={organization.name}
          src={organization.avatarUrl}
          sx={{
            height: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
            width: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
          }}
          slotProps={{
            img: {
              "aria-label": `${organization.name} organization avatar`,
            },
          }}
        />
        <Box sx={{ ml: { md: "1em", sm: "0.75em", xs: "0.5em" } }}>
          <Typography
            component="p"
            sx={{
              fontSize: { md: "1.25rem", sm: "1rem", xs: "0.9rem" },
            }}
            variant="h6"
          >
            {organization.name}
          </Typography>
          <Stack spacing={0.5}>
            <Typography
              sx={{
                fontSize: { md: "0.875rem", sm: "0.8125rem", xs: "0.75rem" },
              }}
              variant="body2"
            >
              {organization.address}
            </Typography>
            {organization.description && (
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { md: "0.875rem", sm: "0.8125rem", xs: "0.75rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                variant="body2"
              >
                {organization.description}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <Chip
          label={organization.isExternal ? "Community" : "School"}
          size="small"
          color="primary"
          variant={organization.isExternal ? "outlined" : "filled"}
          sx={{
            fontSize: { md: "0.75rem", sm: "0.7rem", xs: "0.65rem" },
            height: { md: "24px", sm: "22px", xs: "20px" },
            mr: isSiteAdmin ? 2 : 0,
          }}
        />
        {isSiteAdmin && (
          <Box sx={{ alignItems: "center", display: "flex" }}>
            <IconButton aria-label={tDeleteOrganizationTooltip} onClick={handleMenuClick} size="small">
              <Tooltip placement="top-start" title={tDeleteOrganizationTooltip}>
                <MoreVert fontSize="small" />
              </Tooltip>
            </IconButton>
            <Menu anchorEl={anchorEl} onClick={handleMenuClose} onClose={handleMenuClose} open={Boolean(anchorEl)}>
              <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
                <DeleteIcon sx={{ fontSize: "small", mr: 1 }} />
                {tDelete}
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { OrganizationItem };
